'use client'

import {useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {useId, useState} from 'react'
import {CalendarClock} from 'lucide-react'
import {Button, Input} from '@/design-system'
import {useToast} from '@/components/Toast'
import {api, ApiError} from '@/lib/api/client'

/**
 * The student's own deadline for a saved university.
 *
 * The catalogue holds no university deadline it can vouch for — only a link
 * to the university's page. A student who reads the date there keeps it
 * here, and the dashboard counts down to it like a scholarship's. It is
 * labelled as theirs everywhere it shows.
 */
export function MyDeadline({
  slug,
  initialDate,
  initialLabel,
  formattedDate
}: {
  slug: string
  initialDate: string | null
  initialLabel: string | null
  /** The saved date in the reader's locale, from the server. */
  formattedDate: string | null
}) {
  const t = useTranslations('university')
  const tErrors = useTranslations('errors')
  const router = useRouter()
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [date, setDate] = useState(initialDate ?? '')
  const [label, setLabel] = useState(initialLabel ?? '')
  const [busy, setBusy] = useState(false)
  const formId = useId()

  async function save(next: string | null) {
    setBusy(true)
    try {
      await api.put(`/me/saved/university/${encodeURIComponent(slug)}/deadline`, {
        date: next,
        label: next ? label.trim() || null : null
      })
      setEditing(false)
      if (!next) {
        setDate('')
        setLabel('')
      }
      toast.show(t(next ? 'myDeadlineSaved' : 'myDeadlineCleared'))
      router.refresh()
    } catch (error) {
      toast.show(tErrors(error instanceof ApiError ? error.messageKey : 'internal'), 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!editing) {
    return (
      <div className="u-my-deadline" data-testid="my-deadline">
        <CalendarClock aria-hidden="true" size={18} />
        {initialDate && formattedDate ? (
          <span>
            {t('myDeadlineShown', {date: formattedDate})}
            {initialLabel ? ` · ${initialLabel}` : ''}
          </span>
        ) : (
          <span className="u-my-deadline__none">{t('myDeadlineNone')}</span>
        )}
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          {initialDate ? t('myDeadlineEdit') : t('myDeadlineAdd')}
        </Button>
      </div>
    )
  }

  return (
    <form
      id={formId}
      className="u-my-deadline u-my-deadline--editing"
      onSubmit={(event) => {
        event.preventDefault()
        if (date) void save(date)
      }}
    >
      <Input
        type="date"
        label={t('myDeadlineDate')}
        hint={t('myDeadlineHelp')}
        value={date}
        required
        onChange={(event) => setDate(event.target.value)}
      />
      <Input
        label={t('myDeadlineLabel')}
        placeholder={t('myDeadlineLabelPlaceholder')}
        value={label}
        maxLength={60}
        onChange={(event) => setLabel(event.target.value)}
      />
      <div className="u-my-deadline__actions">
        <Button type="submit" size="sm" disabled={busy || !date}>
          {t('myDeadlineSave')}
        </Button>
        {initialDate ? (
          <Button variant="danger-ghost" size="sm" disabled={busy} onClick={() => void save(null)}>
            {t('myDeadlineRemove')}
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(false)}>
          {t('myDeadlineCancel')}
        </Button>
      </div>
    </form>
  )
}
